import React from 'react';
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import { cn } from '../../utils/cn';

/**
 * ToggleGroup Component
 *
 * A customizable toggle group component built on top of Radix UI's ToggleGroup.
 * It allows for grouping of multiple toggle items.
 * Learn more about Radix UI ToggleGroup: https://www.radix-ui.com/primitives/docs/components/toggle-group
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} [props.className] - Additional CSS classes to apply to the root element
 * @param {React.ReactNode} props.children - The child elements to render within the toggle group
 *
 * @example
 * <ToggleGroupRoot>
 *   <ToggleGroupItem value="option1">Option 1</ToggleGroupItem>
 *   <ToggleGroupItem value="option2">Option 2</ToggleGroupItem>
 * </ToggleGroupRoot>
 */
const ToggleGroupRoot = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> & {
    className?: string;
  }
>(({ className, children, ...props }, ref) => (
  <ToggleGroupPrimitive.Root
    ref={ref}
    className={cn('flex w-fit items-center justify-center', className)}
    {...props}
  >
    {children}
  </ToggleGroupPrimitive.Root>
));

ToggleGroupRoot.displayName = ToggleGroupPrimitive.Root.displayName;

export { ToggleGroupRoot };
